import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAlbums } from '../contexts/AlbumContext';
import PhotoGrid from '../components/PhotoGrid';
import PhotoUploader from '../components/PhotoUploader';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

const PublicAlbum = () => {
  const { shareId } = useParams();
  const { getAlbumByShareId, addPhotoToAlbum } = useAlbums();
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchAlbumData = async () => {
    const albumData = await getAlbumByShareId(shareId);
    setAlbum(albumData);
  };

  useEffect(() => {
    const loadAlbum = async () => {
      await fetchAlbumData();
      setLoading(false);
    };

    loadAlbum();
  }, [shareId, getAlbumByShareId]);

  const handlePhotoUpload = async (files) => {
    if (!album) return;

    setUploading(true);
    try {
      console.log('Starting public upload for files:', files);
      let successCount = 0;
      let skippedCount = 0;

      for (const file of files) {
        console.log('Processing public file:', file.name, file.size, file.type);
        const photoData = {
          file: file,
          uploaderName: 'Guest User'
        };

        const result = await addPhotoToAlbum(album.shareId, photoData);
        if (result.success) {
          console.log('Successfully uploaded to public album:', file.name);
          successCount++;
        } else {
          console.log('Skipped file:', file.name, result.error);
          skippedCount++;
          // Continue with next file without showing error popup
        }
      }

      // Only show success message if any files were uploaded
      if (successCount > 0) {
        console.log(`Upload complete: ${successCount} uploaded, ${skippedCount} skipped`);
      }

      // Refresh album data
      await fetchAlbumData();
    } catch (error) {
      console.error('Public upload process failed:', error);
      // Don't show error popup, just log it
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-light">Loading album...</p>
        </div>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-light text-black mb-3">Album Not Found</h1>
          <p className="text-gray-600 font-light">This album may have been deleted or the link is invalid.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Create Account CTA Bar */}
      <div className="bg-black text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-center sm:text-left">
              <span className="text-sm font-light">
                Want to create your own photo albums? 
              </span>
            </div>
            <Link
              to="/signup"
              className="bg-white text-black px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-100 transition-all whitespace-nowrap"
            >
              Create Free Account
            </Link>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-black mb-4 tracking-tight px-4">
              {album.title}
            </h1>
            {album.description && (
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto font-light leading-relaxed px-4 text-sm sm:text-base">
                {album.description}
              </p>
            )}
            
            {/* Mobile-optimized stats */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-500 font-light px-4">
              <div className="flex items-center gap-4 sm:gap-6">
                <span className="bg-gray-100 px-3 py-1 rounded-full">
                  {album.photos.length} photos
                </span>
                <span className="bg-gray-100 px-3 py-1 rounded-full">
                  by {album.ownerName}
                </span>
              </div>
              {album.eventDate && (
                <span className="bg-gray-100 px-3 py-1 rounded-full">
                  {format(new Date(album.eventDate), 'MMM d, yyyy')}
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Photo Uploader */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 sm:mb-12"
        >
          <PhotoUploader
            onUpload={handlePhotoUpload}
            uploading={uploading}
            albumTitle={album.title}
          />
        </motion.div>

        {/* Photo Grid - Public albums don't show delete buttons */}
        <PhotoGrid
          photos={album.photos}
          albumTitle={album.title}
          isPublic={true}
          album={null} // Don't pass album to prevent delete buttons
        />

        {album.photos.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center py-16 sm:py-20 border border-gray-200 rounded-lg mx-4 sm:mx-0"
          >
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <div className="grid grid-cols-2 gap-1 w-6 h-6 sm:w-8 sm:h-8">
                <div className="bg-gray-400 rounded-sm"></div>
                <div className="bg-gray-400 rounded-sm"></div>
                <div className="bg-gray-400 rounded-sm"></div>
                <div className="bg-gray-400 rounded-sm"></div>
              </div>
            </div>
            <h3 className="text-lg sm:text-xl font-light text-black mb-3 px-4">Be the first to share!</h3>
            <p className="text-gray-600 max-w-md mx-auto font-light px-4 text-sm sm:text-base">
              Upload your photos from this event to get the album started. Others will see them here too.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PublicAlbum;